/**
 * Manage Page - Settings Hub with URL-based routing
 *
 * Routes:
 * - /manage - Dashboard
 * - /manage/training - Face recognition (all roles)
 * - /manage/teams - Team CRUD with aliases (MANAGER, PLANNER)
 * - /manage/persons - Person CRUD, aliases, merge (MANAGER, PLANNER)
 * - /manage/unlinked - Persons without team assignment (MANAGER, PLANNER)
 * - /manage/company - Company hub (MANAGER only)
 * - /manage/company/users - User management
 * - /manage/company/ftp-targets - FTP output targets
 * - /manage/company/profiles - Processing profiles
 * - /manage/company/settings - Company settings
 * - /manage/company/caption-builder - Caption builder config
 */

import { useState, useCallback, useEffect } from 'react';
import { usePermissions } from '../hooks';
import { useSetTopBar } from '../layouts/ShellContext';
import TrainingReview from './TrainingReview';
import {
  ManageNav,
  ManageDashboard,
  TeamList,
  PersonList,
  MergePersonsModal,
  CompanyDashboard,
  UserList,
  FtpTargetList,
  ProfileList,
  CompanySettings,
  CaptionBuilderSettings,
  UnlinkedPlayers,
} from '../features/manage/components';
import CaptionProfilesEditor from '../features/manage/components/CaptionProfilesEditor';
import { fetchPersons, fetchTeams } from '../api';
import styles from './Manage.module.css';

export default function Manage({ onNavigate }) {
  const { canManagePersons, canManageTeams, canManageCompany } = usePermissions();
  const [navCounts, setNavCounts] = useState({});

  // Get current path from window.location and hash
  const getPathInfo = () => {
    const pathname = window.location.pathname;
    const hash = window.location.hash.replace('#', '');

    // Support both URL-based (/manage/teams) and hash-based (/manage#teams) routing
    if (pathname.startsWith('/manage/')) {
      const segments = pathname.replace('/manage/', '').split('/').filter(Boolean);
      return { section: segments[0] || '', subSection: segments[1] || '' };
    }

    // Hash-based routing for backwards compatibility
    if (hash) {
      const segments = hash.split('/').filter(Boolean);
      return { section: segments[0] || '', subSection: segments[1] || '' };
    }

    return { section: '', subSection: '' };
  };

  const [pathInfo, setPathInfo] = useState(getPathInfo);

  // Listen for hash changes
  useEffect(() => {
    const handler = () => setPathInfo(getPathInfo());
    window.addEventListener('hashchange', handler);
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('hashchange', handler);
      window.removeEventListener('popstate', handler);
    };
  }, []);

  // Tab counts: persons / teams / unlinked. Cheap parallel fetch on
  // mount; tabs render fine without them, so failures are silent.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      canManagePersons ? fetchPersons({ page_size: 1 }) : Promise.resolve(null),
      canManageTeams ? fetchTeams({ page_size: 1 }) : Promise.resolve(null),
      canManagePersons ? fetchPersons({ page_size: 1, unlinked: true }) : Promise.resolve(null),
    ]).then(([personsRes, teamsRes, unlinkedRes]) => {
      if (cancelled) return;
      const personsCount = personsRes.status === 'fulfilled' ? personsRes.value?.count : null;
      const teamsCount = teamsRes.status === 'fulfilled' ? teamsRes.value?.count : null;
      const unlinkedCount = unlinkedRes.status === 'fulfilled' ? unlinkedRes.value?.count : null;
      setNavCounts({
        persons: personsCount ?? undefined,
        teams: teamsCount ?? undefined,
        unlinked: unlinkedCount ?? undefined,
      });
    });
    return () => { cancelled = true; };
  }, [canManagePersons, canManageTeams]);

  const currentSection = pathInfo.section;
  const currentSubSection = pathInfo.subSection;
  const subPath = currentSubSection ? '/' + currentSubSection : '';
  const path = currentSection ? `/manage/${currentSection}${subPath}` : '/manage';

  // Reflect the current subroute in the top-bar breadcrumb. Each segment
  // is clickable, so a Manage / Company / Profiles flow lets the user
  // jump back to Company without going through Manage first.
  const setTopBar = useSetTopBar();
  useEffect(() => {
    const sectionLabels = {
      persons: 'Persons',
      teams: 'Teams',
      unlinked: 'Unlinked',
      training: 'Training',
      company: 'Company',
    };
    const subLabels = {
      users: 'Users',
      'ftp-targets': 'FTP targets',
      profiles: 'Profiles',
      settings: 'Settings',
      'caption-builder': 'Caption builder',
      'caption-actions': 'Caption actions',
    };
    const segments = [];
    if (currentSection) {
      segments.push({
        label: sectionLabels[currentSection] || currentSection,
        // Only make the parent segment clickable when there's a sub-route
        // below it. Otherwise the user is already on that section.
        href: currentSubSection ? `#${currentSection}` : undefined,
      });
    }
    if (currentSubSection) {
      segments.push({
        label: subLabels[currentSubSection] || currentSubSection,
      });
    }
    setTopBar({ breadcrumb: segments.length ? segments : null });
    return () => setTopBar(null);
  }, [currentSection, currentSubSection, setTopBar]);

  // Merge modal state
  const [mergePersons, setMergePersons] = useState(null);

  // Handle merge action from PersonList
  const handleMerge = useCallback((persons) => {
    setMergePersons(persons);
  }, []);

  const handleMergeSuccess = useCallback(() => {
    setMergePersons(null);
    // PersonList will refetch on its own
  }, []);

  // Render content based on route
  const renderContent = () => {
    // Company sub-routes
    if (currentSection === 'company') {
      if (!canManageCompany) {
        return <AccessDenied />;
      }

      switch (currentSubSection) {
        case 'users':
          return <UserList />;
        case 'ftp-targets':
          return <FtpTargetList />;
        case 'profiles':
          return <ProfileList />;
        case 'settings':
          return <CompanySettings />;
        case 'caption-builder':
          return <CaptionProfilesEditor />;
        case 'caption-actions':
          return <CaptionBuilderSettings />;
        default:
          return <CompanyDashboard />;
      }
    }

    // Top-level routes
    switch (currentSection) {
      case 'training':
        return <TrainingReview />;
      case 'teams':
        if (!canManageTeams) return <AccessDenied />;
        return <TeamList />;
      case 'persons':
        if (!canManagePersons) return <AccessDenied />;
        return <PersonList onMerge={handleMerge} />;
      case 'unlinked':
        if (!canManagePersons) return <AccessDenied />;
        return <UnlinkedPlayers />;
      default:
        return <ManageDashboard />;
    }
  };

  return (
    <div className={styles.page}>
      <ManageNav
        currentPath={path}
        canManagePersons={canManagePersons}
        canManageTeams={canManageTeams}
        canManageCompany={canManageCompany}
        counts={navCounts}
      />
      <main className={styles.content}>
        {renderContent()}
      </main>

      {/* Merge Persons Modal */}
      {mergePersons?.length > 1 && (
        <MergePersonsModal
          persons={mergePersons}
          onClose={() => setMergePersons(null)}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
}

function AccessDenied() {
  return (
    <div className={styles.accessDenied}>
      <h2>Access Denied</h2>
      <p>You don&apos;t have permission to view this section.</p>
    </div>
  );
}
